import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkStats, SmartLinkStatsSummary } from 'fym-shared/src/smartlinks/stats';
import { musicServicesById } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { Segmented } from '../components/primitives/Segmented';
import { formatServiceName } from '../../smartLinks/stats/formatServiceName';
import { formatCountryName } from '../../smartLinks/stats/formatCountryName';
import { HomeMeterList } from './HomeMeterList';
import { meterRows } from './meterRows';
import { artistHomeMessages as messages } from './artistHomeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TOP_ROWS = 3;

type TopListId = 'pages' | 'services' | 'countries';

interface SmartlinksTopListsProps {
  stats: SmartLinkStats;
  summaries: SmartLinkStatsSummary[];
  nameById: Map<number, string>;
}

/**
 * Top pages by clicks, services by clicks and countries (design a1). On a phone one list shows at
 * a time, under a three-way switch (design c1).
 */
const SmartlinksTopListsComponent: React.FC<SmartlinksTopListsProps> = ({ stats, summaries, nameById }) => {
  const intl = useIntl();
  const [mobileList, setMobileList] = useState<TopListId>('pages');

  const lists = useMemo(
    () => ({
      pages: meterRows(
        summaries.map((summary) => ({
          key: String(summary.smartLinkId),
          name: nameById.get(summary.smartLinkId) ?? `#${summary.smartLinkId}`,
          value: summary.clicks,
        })),
        TOP_ROWS
      ),
      services: meterRows(
        stats.services.map((service) => ({
          key: service.key,
          name: formatServiceName(service.key),
          value: service.clicks,
          color: musicServicesById[service.key]?.color.primary,
        })),
        TOP_ROWS
      ),
      countries: meterRows(
        stats.countries.map((country) => ({
          key: country.key,
          name: formatCountryName(intl, country.key),
          value: country.clicks,
        })),
        TOP_ROWS
      ),
    }),
    [intl, nameById, stats.countries, stats.services, summaries]
  );

  const options = useMemo(
    () => [
      { value: 'pages' as const, label: intl.formatMessage(messages.smartlinksTabPages) },
      { value: 'services' as const, label: intl.formatMessage(messages.smartlinksTabServices) },
      { value: 'countries' as const, label: intl.formatMessage(messages.smartlinksTabCountries) },
    ],
    [intl]
  );

  return (
    <>
      <div className={`${styles.lists} d-none d-md-grid`}>
        <HomeMeterList title={intl.formatMessage(messages.smartlinksTopPages)} rows={lists.pages} />
        <HomeMeterList title={intl.formatMessage(messages.smartlinksServices)} rows={lists.services} />
        <HomeMeterList title={intl.formatMessage(messages.smartlinksCountries)} rows={lists.countries} />
      </div>
      <div className="d-md-none">
        <Segmented options={options} value={mobileList} onChange={setMobileList} className={styles.listTabs} />
        <HomeMeterList rows={lists[mobileList]} />
      </div>
    </>
  );
};

export const SmartlinksTopLists = TypedMemo(SmartlinksTopListsComponent);
